/**
 * Which merge-diff rows can be applied without a full promote.
 * Keep identity fields on the catalog; default selection is spec leaves
 * so one-click Apply matches "specs only".
 */

export const BLOCKED_MERGE_PATHS = new Set([
  'sku',
  'upc',
  'ean',
  'gtin',
  'brand',
  'brandId',
  'id',
]);

export function isBlockedMergePath(path: string): boolean {
  const top = String(path || '').split('.')[0];
  return BLOCKED_MERGE_PATHS.has(top);
}

export function isSpecificationPath(path: string): boolean {
  return path === 'specifications' || path.startsWith('specifications.');
}

export function isSelectableMergeField(field: { path: string; kind: string }): boolean {
  return (field.kind === 'added' || field.kind === 'changed') && !isBlockedMergePath(field.path);
}

export function defaultSelectedMergePaths(
  fields: Array<{ path: string; kind: string }>,
): string[] {
  return fields.filter((f) => isSelectableMergeField(f) && isSpecificationPath(f.path)).map((f) => f.path);
}

export type MergeSelectionPayload = {
  preset?: 'specifications' | 'added_and_changed';
  paths?: string[];
};

export type MergeSelectionState = {
  selectedPaths: string[];
  selectablePaths: string[];
  payload: MergeSelectionPayload;
};

/** When every selected row is a spec leaf and none were unchecked, use the whole-tree preset. */
export function selectedMergePayload(
  selected: string[],
  fields: Array<{ path: string; kind: string }>,
): MergeSelectionPayload {
  const unique = Array.from(new Set(selected.filter(Boolean)));
  if (unique.length === 0) return { paths: [] };
  const specDefaults = defaultSelectedMergePaths(fields);
  const allSpecs = unique.every(isSpecificationPath);
  const sameAsDefaults =
    allSpecs &&
    unique.length === specDefaults.length &&
    specDefaults.every((p) => unique.includes(p));
  if (sameAsDefaults) return { preset: 'specifications' };
  return { paths: unique };
}

export function buildMergeSelectionState(
  fields: Array<{ path: string; kind: string }>,
  selectedPaths?: Iterable<string>,
): MergeSelectionState {
  const selectablePaths = fields.filter(isSelectableMergeField).map((f) => f.path);
  const selected = selectedPaths
    ? selectablePaths.filter((p) => new Set(selectedPaths).has(p))
    : defaultSelectedMergePaths(fields);
  return {
    selectedPaths: selected,
    selectablePaths,
    payload: selectedMergePayload(selected, fields),
  };
}

/**
 * Body for detail promote/approve. Omit the include-list unless the merge
 * preview loaded — an empty-fields fallback would close the row with no writes.
 */
export function resolvePromoteMergeSelection(opts: {
  hasCanonical: boolean;
  previewReady: boolean;
  isNoop: boolean;
  selection: MergeSelectionState | null;
}): MergeSelectionPayload | undefined {
  if (!opts.hasCanonical || !opts.previewReady) return undefined;
  if (opts.isNoop) return { paths: [] };
  return opts.selection?.payload;
}
